/**
 * Matching bank rows against what the bookkeeping expects (product spec
 * section 9, "Matching & questions") and receipts against imported bank rows
 * (section 7, pipeline step 3). Pure scoring 0–100 with Swedish reasons:
 *
 *   amount   exact 60 · foreign currency within 3 % (needs a name overlap) 45
 *   date     inside the window 30 · inside the extended window 10 · else no candidate
 *   name     merchant token in the bank text 10
 *   invoice  OCR / invoice number in the bank text 10
 *
 * Windows are asymmetric: a card purchase shows up on the account from one
 * day before the receipt date (time zones) up to seven bank days after;
 * invoices settle around their due date; reimbursements come later.
 * `decide` auto-links only when the top score is ≥ 80 and the runner-up is at
 * least 20 points behind; otherwise the top three are shown.
 */
import type { Ore } from '../money';
import { formatSek } from '../money';
import { merchantKeyOf, merchantTokensOverlap } from '../merchants/normalize';
import { formatSwedishDate } from '../periods/deadlines';
import type { OpenItem } from '../inbox/types';
import type { BankRow, IsoDate, Uuid } from '../types';

export const AUTO_LINK_MIN_SCORE = 80;
export const AUTO_LINK_MIN_GAP = 20;
export const ASK_TOP_N = 3;
export const FX_TOLERANCE = 0.03;

export const SETTLEMENT_WINDOW = { before: 1, after: 7, extendedBefore: 3, extendedAfter: 14 } as const;
export const INVOICE_WINDOW = { aroundDue: 7, extended: 30 } as const;
export const REIMBURSEMENT_WINDOW = { after: 30, extendedAfter: 90 } as const;
/** Default payment term when an invoice has no due date. */
export const DEFAULT_PAYMENT_TERM_DAYS = 30;
/** Two rows of an own transfer may be booked on different days at the two banks. */
export const TRANSFER_PAIR_MAX_DAYS = 2;

/**
 * A payment smaller than what an invoice or utlägg still owes: an instalment.
 * Scored below an exact amount and only with the name or the reference in the
 * bank text, because an amount alone proves nothing about a partial payment.
 */
const SCORE = { exact: 60, fx: 45, partial: 35, window: 30, extended: 10, name: 10, reference: 10 } as const;

/** Sum-combinations are limited to this many open items (2 or 3 invoices in one payment). */
export const MAX_COMBINATION_ITEMS = 3;
/** Money back from a merchant is looked for this long after the purchase. */
export const REFUND_WINDOW = { after: 30, extendedAfter: 90 } as const;
/** A credit note is looked for this long after the purchase or invoice it reduces. */
export const CREDIT_NOTE_WINDOW_DAYS = 180;

export interface Candidate {
  item: OpenItem;
  score: number;
  /** Swedish one-liner: "Samma belopp, 2 dagar efter kvittot · säljaren i banktexten". */
  reason: string;
  /** The row pays only part of what the item still owes. */
  partial?: boolean;
}

export interface Decision {
  auto: Candidate | null;
  ask: Candidate[];
}

const DAY_MS = 86_400_000;

export function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Digit runs of five or more (OCR numbers, invoice numbers). */
export function referenceTokens(text: string): Set<string> {
  return new Set((text.match(/\d{5,}/g) ?? []).map((t) => t.replace(/^0+/, '') || '0'));
}

/** Kinds that may be paid in instalments: an invoice, or an utlägg paid back bit by bit. */
function allowsPartial(kind: OpenItem['kind']): boolean {
  switch (kind) {
    case 'customer_invoice':
    case 'supplier_invoice':
    case 'reimbursement':
      return true;
    case 'settlement':
      return false;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown open item kind ${String(exhaustive)}`);
    }
  }
}

function amountScore(rowAmount: Ore, expected: Ore, kind: OpenItem['kind'], foreign: boolean, evidence: boolean): { points: number; text: string; partial: boolean } | null {
  if (rowAmount === expected) return { points: SCORE.exact, text: 'Samma belopp', partial: false };
  if (Math.sign(rowAmount) !== Math.sign(expected)) return null;
  if (foreign && evidence) {
    const diff = Math.abs(Math.abs(rowAmount) - Math.abs(expected));
    if (diff <= Math.round(Math.abs(expected) * FX_TOLERANCE)) return { points: SCORE.fx, text: 'Belopp inom 3 % (utländsk valuta)', partial: false };
  }
  if (evidence && allowsPartial(kind) && Math.abs(rowAmount) < Math.abs(expected)) {
    return { points: SCORE.partial, text: `Delbetalning: ${formatSek(Math.abs(rowAmount))} kr av ${formatSek(Math.abs(expected))} kr`, partial: true };
  }
  return null;
}

function relativeText(delta: number, anchor: string): string {
  if (delta === 0) return `samma dag som ${anchor}`;
  const n = Math.abs(delta);
  const unit = n === 1 ? 'dag' : 'dagar';
  return delta > 0 ? `${n} ${unit} efter ${anchor}` : `${n} ${unit} före ${anchor}`;
}

function dateScore(item: OpenItem, rowDate: IsoDate): { points: number; text: string } | null {
  switch (item.kind) {
    case 'settlement': {
      const d = daysBetween(item.date, rowDate);
      if (d >= -SETTLEMENT_WINDOW.before && d <= SETTLEMENT_WINDOW.after) return { points: SCORE.window, text: relativeText(d, 'kvittot') };
      if (d >= -SETTLEMENT_WINDOW.extendedBefore && d <= SETTLEMENT_WINDOW.extendedAfter) return { points: SCORE.extended, text: relativeText(d, 'kvittot') };
      return null;
    }
    case 'customer_invoice':
    case 'supplier_invoice': {
      if (daysBetween(item.date, rowDate) < -1) return null;
      const due = item.dueDate ?? addDays(item.date, DEFAULT_PAYMENT_TERM_DAYS);
      const d = daysBetween(due, rowDate);
      if (Math.abs(d) <= INVOICE_WINDOW.aroundDue) return { points: SCORE.window, text: relativeText(d, 'förfallodagen') };
      if (Math.abs(d) <= INVOICE_WINDOW.extended) return { points: SCORE.extended, text: relativeText(d, 'förfallodagen') };
      return null;
    }
    case 'reimbursement': {
      const d = daysBetween(item.date, rowDate);
      if (d < -1) return null;
      if (d <= REIMBURSEMENT_WINDOW.after) return { points: SCORE.window, text: relativeText(d, 'utlägget') };
      if (d <= REIMBURSEMENT_WINDOW.extendedAfter) return { points: SCORE.extended, text: relativeText(d, 'utlägget') };
      return null;
    }
    default: {
      const exhaustive: never = item.kind;
      throw new Error(`Unknown open item kind ${String(exhaustive)}`);
    }
  }
}

/** Whether the item's OCR/invoice number appears in the bank text. */
function referenceHit(item: OpenItem, text: string): boolean {
  if (!item.reference) return false;
  const wanted = item.reference.replace(/\D/g, '').replace(/^0+/, '');
  return wanted !== '' && referenceTokens(text).has(wanted);
}

/** Score one bank row against one expected item; null when it is no candidate. */
export function scoreCandidate(row: Pick<BankRow, 'amountOre' | 'bookingDate' | 'transactionDate' | 'text'>, item: OpenItem): Candidate | null {
  const nameOverlap = item.counterparty !== '' && merchantTokensOverlap(item.counterparty, row.text);
  const refHit = referenceHit(item, row.text);
  const amount = amountScore(row.amountOre, item.amountOre, item.kind, item.foreignCurrency === true, nameOverlap || refHit);
  if (!amount) return null;
  const rowDate = row.transactionDate ?? row.bookingDate;
  const date = dateScore(item, rowDate) ?? (row.transactionDate ? dateScore(item, row.bookingDate) : null);
  if (!date) return null;
  const parts = [`${amount.text}, ${date.text}`];
  let score = amount.points + date.points;
  if (nameOverlap) {
    score += SCORE.name;
    parts.push(item.kind === 'settlement' ? 'säljaren i banktexten' : 'namnet i banktexten');
  }
  if (refHit) {
    score += SCORE.reference;
    parts.push(`referensen ${item.reference} i banktexten`);
  }
  return { item, score: Math.min(100, score), reason: parts.join(' · '), ...(amount.partial ? { partial: true } : {}) };
}

// ---- One payment, several invoices ------------------------------------------------

export interface CombinationCandidate {
  /** Two or three open items of one counterparty whose remaining amounts add up to the row. */
  items: OpenItem[];
  score: number;
  reason: string;
}

/**
 * Sum-combinations: a bank row equal to what two or three open invoices (or
 * utlägg) of the same counterparty still owe, together. Only with the name
 * or every reference in the bank text — a sum that happens to fit is not a
 * match — and always presented for review, never auto-linked, because the
 * alternative reading (one invoice plus something else) needs a human.
 * Groups are found by counterparty key first, so the search stays tiny.
 */
export function rankCombinations(row: Pick<BankRow, 'amountOre' | 'bookingDate' | 'transactionDate' | 'text'>, openItems: readonly OpenItem[]): CombinationCandidate[] {
  const target = row.amountOre;
  if (target === 0) return [];
  const byKey = new Map<string, OpenItem[]>();
  for (const item of openItems) {
    if (!allowsPartial(item.kind) || Math.sign(item.amountOre) !== Math.sign(target)) continue;
    if (Math.abs(item.amountOre) >= Math.abs(target)) continue;
    const key = merchantKeyOf(item.counterparty);
    if (!key) continue;
    const list = byKey.get(key) ?? [];
    list.push(item);
    byKey.set(key, list);
  }
  const rowDate = row.transactionDate ?? row.bookingDate;
  const out: CombinationCandidate[] = [];
  for (const group of byKey.values()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => a.date.localeCompare(b.date) || a.verifikationId.localeCompare(b.verifikationId));
    const found: OpenItem[][] = [];
    const walk = (start: number, chosen: OpenItem[], sum: Ore) => {
      if (chosen.length >= 2 && sum === target) {
        found.push([...chosen]);
        return;
      }
      if (chosen.length >= MAX_COMBINATION_ITEMS) return;
      for (let i = start; i < sorted.length; i++) {
        const next = sum + sorted[i]!.amountOre;
        if (Math.abs(next) > Math.abs(target)) continue;
        chosen.push(sorted[i]!);
        walk(i + 1, chosen, next);
        chosen.pop();
      }
    };
    walk(0, [], 0);
    for (const items of found) {
      const nameOverlap = items.every((i) => i.counterparty !== '' && merchantTokensOverlap(i.counterparty, row.text));
      const allRefs = items.every((i) => referenceHit(i, row.text));
      if (!nameOverlap && !allRefs) continue;
      const inWindow = items.every((i) => dateScore(i, rowDate) !== null);
      const score = SCORE.exact + (inWindow ? SCORE.extended : 0) + (nameOverlap ? SCORE.name : 0) + (allRefs ? SCORE.reference : 0);
      const labels = items.map((i) => formatSek(Math.abs(i.amountOre))).join(' + ');
      out.push({ items, score, reason: `Beloppet är summan av ${items.length} fakturor (${labels} kr)${nameOverlap ? ' · namnet i banktexten' : ''}${allRefs ? ' · referenserna i banktexten' : ''}` });
    }
  }
  return out.sort((a, b) => b.score - a.score || a.items.length - b.items.length);
}

// ---- Money back ---------------------------------------------------------------------

/** A booked purchase that money coming back from the same merchant could refund. */
export interface RefundablePurchase {
  verifikationId: Uuid;
  /** Gross still refundable (the purchase minus refunds already booked), positive. */
  remainingOre: Ore;
  date: IsoDate;
  counterparty: string;
}

export interface RefundCandidate {
  purchase: RefundablePurchase;
  score: number;
  reason: string;
  partial: boolean;
}

/**
 * Money in from a merchant the company bought from: a refund of that purchase.
 * The merchant's name in the bank text is mandatory — the same amount coming
 * in from somewhere else is a sale, not a refund — and the row must land
 * within three months of the purchase. Always reviewed, never auto-booked.
 */
export function rankRefundCandidates(row: Pick<BankRow, 'amountOre' | 'bookingDate' | 'transactionDate' | 'text'>, purchases: readonly RefundablePurchase[]): RefundCandidate[] {
  if (row.amountOre <= 0) return [];
  const rowDate = row.transactionDate ?? row.bookingDate;
  const out: RefundCandidate[] = [];
  for (const purchase of purchases) {
    if (purchase.remainingOre <= 0 || row.amountOre > purchase.remainingOre) continue;
    if (!purchase.counterparty || !merchantTokensOverlap(purchase.counterparty, row.text)) continue;
    const d = daysBetween(purchase.date, rowDate);
    if (d < -1 || d > REFUND_WINDOW.extendedAfter) continue;
    const partial = row.amountOre < purchase.remainingOre;
    const amount = partial ? SCORE.partial : SCORE.exact;
    const date = d <= REFUND_WINDOW.after ? SCORE.window : SCORE.extended;
    const amountText = partial ? `${formatSek(row.amountOre)} kr av köpets ${formatSek(purchase.remainingOre)} kr` : 'Samma belopp som köpet';
    out.push({
      purchase,
      score: Math.min(100, amount + date + SCORE.name),
      reason: `${amountText}, ${relativeText(d, 'köpet')} · säljaren i banktexten`,
      partial,
    });
  }
  return out.sort((a, b) => b.score - a.score || b.purchase.date.localeCompare(a.purchase.date));
}

/**
 * The purchase or invoice a credit note reduces: the same merchant, an amount
 * no larger than what is left, within six months. Exact amounts first, then
 * the newest. The document itself carries no bank text, so the match is on
 * the merchant name read off it.
 */
export function rankCreditNoteTargets(
  doc: { amountOre: Ore; date: IsoDate; merchant: string },
  purchases: readonly RefundablePurchase[],
): RefundCandidate[] {
  if (doc.amountOre <= 0 || !doc.merchant) return [];
  const out: RefundCandidate[] = [];
  for (const purchase of purchases) {
    if (purchase.remainingOre <= 0 || doc.amountOre > purchase.remainingOre) continue;
    if (!purchase.counterparty || !merchantTokensOverlap(purchase.counterparty, doc.merchant)) continue;
    const d = daysBetween(purchase.date, doc.date);
    if (d < -1 || d > CREDIT_NOTE_WINDOW_DAYS) continue;
    const partial = doc.amountOre < purchase.remainingOre;
    out.push({
      purchase,
      score: Math.min(100, (partial ? SCORE.partial : SCORE.exact) + (d <= REFUND_WINDOW.after ? SCORE.window : SCORE.extended) + SCORE.name),
      reason: `${partial ? `${formatSek(doc.amountOre)} kr av ${formatSek(purchase.remainingOre)} kr` : 'Samma belopp'}, ${relativeText(d, 'köpet')} · samma säljare`,
      partial,
    });
  }
  return out.sort((a, b) => b.score - a.score || b.purchase.date.localeCompare(a.purchase.date));
}

/** All candidates for a row, best first. Already linked/booked rows have none. */
export function rankCandidates(row: BankRow, openItems: readonly OpenItem[]): Candidate[] {
  if (row.status !== 'open') return [];
  const out: Candidate[] = [];
  for (const item of openItems) {
    const c = scoreCandidate(row, item);
    if (c) out.push(c);
  }
  return out.sort((a, b) => b.score - a.score || a.item.date.localeCompare(b.item.date));
}

/** Auto-link only when the winner is clear; otherwise ask with the top three. */
export function decide(candidates: readonly Candidate[]): Decision {
  const [top, second] = candidates;
  if (top && top.score >= AUTO_LINK_MIN_SCORE && (!second || second.score <= top.score - AUTO_LINK_MIN_GAP)) {
    return { auto: top, ask: [] };
  }
  return { auto: null, ask: candidates.slice(0, ASK_TOP_N) };
}

// ---- Receipt → bank row (pipeline step 3) -------------------------------------

export interface ReceiptToMatch {
  /** Gross amount as printed, always positive. */
  amountOre: Ore;
  date: IsoDate;
  merchant: string;
  /** ISO 4217; anything but SEK enables the 3 % window. */
  currency: string;
  /** Money out (purchase) or in (sale/refund). */
  direction: 'out' | 'in';
  /** Bank sources the receipt may have been paid from; empty = any. */
  allowedSourceIds?: ReadonlySet<Uuid> | undefined;
}

export interface RowCandidate {
  row: BankRow;
  score: number;
  reason: string;
}

/** Rank open bank rows for a receipt using the settlement window. */
export function rankBankRowsForReceipt(receipt: ReceiptToMatch, rows: readonly BankRow[]): RowCandidate[] {
  const foreign = receipt.currency.toUpperCase() !== 'SEK';
  const expected: OpenItem = {
    kind: 'settlement',
    verifikationId: '',
    ledgerAccount: 0,
    amountOre: receipt.direction === 'out' ? -Math.abs(receipt.amountOre) : Math.abs(receipt.amountOre),
    date: receipt.date,
    counterparty: receipt.merchant,
    foreignCurrency: foreign,
  };
  const out: RowCandidate[] = [];
  for (const row of rows) {
    if (row.status !== 'open') continue;
    if (receipt.allowedSourceIds && receipt.allowedSourceIds.size > 0 && !receipt.allowedSourceIds.has(row.paymentSourceId)) continue;
    const c = scoreCandidate(row, expected);
    if (c) out.push({ row, score: c.score, reason: c.reason });
  }
  return out.sort((a, b) => b.score - a.score || a.row.bookingDate.localeCompare(b.row.bookingDate));
}

export function decideRows(candidates: readonly RowCandidate[]): { auto: RowCandidate | null; ask: RowCandidate[] } {
  const [top, second] = candidates;
  if (top && top.score >= AUTO_LINK_MIN_SCORE && (!second || second.score <= top.score - AUTO_LINK_MIN_GAP)) {
    return { auto: top, ask: [] };
  }
  return { auto: null, ask: candidates.slice(0, ASK_TOP_N) };
}

// ---- Own transfers ---------------------------------------------------------------

const TRANSFER_TEXT = /\b(?:överföring|overforing|överf|till konto|från konto|egen överföring|internöverföring|till eget konto)\b/i;

export function looksLikeOwnTransferText(text: string): boolean {
  return TRANSFER_TEXT.test(text);
}

/**
 * The other half of an own transfer: opposite sign, equal amount, another
 * payment source, at most two bank days apart. Text markers ("Överföring",
 * "Till konto") break ties toward the closest date.
 */
export function findTransferPair(row: BankRow, rows: readonly BankRow[]): BankRow | undefined {
  let best: { row: BankRow; distance: number } | undefined;
  for (const other of rows) {
    if (other.id === row.id || other.status !== 'open') continue;
    if (other.paymentSourceId === row.paymentSourceId) continue;
    if (other.amountOre !== -row.amountOre) continue;
    const distance = Math.abs(daysBetween(row.bookingDate, other.bookingDate));
    if (distance > TRANSFER_PAIR_MAX_DAYS) continue;
    if (!best || distance < best.distance) best = { row: other, distance };
  }
  return best?.row;
}

/** "Avstämd mot A12" – the reason shown when a row was linked to an existing booking. */
export function reconciledText(v: { series: string; number: number }): string {
  return `Avstämd mot ${v.series}${v.number}`;
}

/** "Kvitto som stämmer: Clas Ohlson, 625,00 kr, bokfört 4 mars" – the summary in a match card. */
export function candidateSummary(c: Candidate, relativeTo: IsoDate): string {
  return `${c.item.counterparty || 'Okänd'}, ${formatSek(Math.abs(c.item.amountOre))} kr, bokfört ${formatSwedishDate(c.item.date, { relativeTo })}`;
}
